// @Version: 1.0.5
import { Component, EventEmitter, forwardRef, Input, Output, OnDestroy, signal, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { debounceTime, map } from 'rxjs/operators';
import { ErrorMessagesService } from 'src/app/shared/services/errorMessages/error-messages.service';
import { ValidatorsService } from 'src/app/shared/services/validators/validators.service';
import { FIELD_DESIGN_MODE, IErrorCodes, IFieldItem, ITEMTYPE, VALIDATORS } from '../field-item.model';
import { FC_TEXT_INPUT } from './text-input.model';
import { TRANSLATE_PATH } from 'src/app/shared/shared.model';
import { Subscription } from 'rxjs';

@UntilDestroy()
@Component({
   selector: 'app-field-item-text-input',
   templateUrl: './text-input.component.html',
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         useExisting: forwardRef(() => FieldItemTextInputComponent),
         multi: true
      },
      {
         provide: NG_VALIDATORS,
         useExisting: forwardRef(() => FieldItemTextInputComponent),
         multi: true
      },
      ErrorMessagesService
   ],
   standalone: false
})
export class FieldItemTextInputComponent implements ControlValueAccessor, OnDestroy, Validators {

   public FIELD_DESIGN_MODE = FIELD_DESIGN_MODE;
   public VALIDATORS = VALIDATORS;
   public FC_TEXT_INPUT = FC_TEXT_INPUT; 

   public done$$ = signal(false);

   public fgText: FormGroup = this.fb.group({});

   public hideRequireMaker: boolean = false;

   private fieldItem_: IFieldItem = {} as IFieldItem;
   private errorCodes_: IErrorCodes = {} as IErrorCodes;
   private sub_?: Subscription;
   
   @Input() clearValueIfDisabled: boolean = true;

   @Input() 
   set fieldItem (value: IFieldItem) {
        this.fieldItem_ = value || {};
   }
   get fieldItem (): IFieldItem {
     return this.fieldItem_;
   }

   @Input()
   set value (val: string) {  
     if (typeof this.fgText.get(FC_TEXT_INPUT.TEXT) != 'undefined' && val) return;
     this.fgText.get(FC_TEXT_INPUT.TEXT)?.setValue(val);
   }
   get value (): string {
      return this.fgText.get(FC_TEXT_INPUT.TEXT)?.value ?? '';
   }   

   @Output() isRequiredMarker: EventEmitter<boolean> = new EventEmitter<boolean>();

   get errorCodes (): IErrorCodes {
      return this.errorCodes_;
   }

   constructor (private fb: FormBuilder, private sErrorMessages: ErrorMessagesService, private sValidators: ValidatorsService) { }
   
   ngOnChanges(changes: SimpleChanges): void {
      if (changes && changes.fieldItem &&  (JSON.stringify(changes.fieldItem.currentValue) != JSON.stringify(changes.fieldItem.previousValue))) {
         this.errorCodes_ = this.sErrorMessages.setErrorCodes(changes.fieldItem.currentValue.attribute_2 ?? '', TRANSLATE_PATH.FIELD_ITEM_ERROR);
         this.initFormGroup(changes.fieldItem.currentValue);
         this.onValidationChange();
         this.done$$.set(true);
      }
   }
   
   ngOnDestroy (): void {
      this.reset();
   }

   validate (control: AbstractControl): ValidationErrors | null {
      return this.fgText.get(FC_TEXT_INPUT.TEXT) ? this.fgText.get(FC_TEXT_INPUT.TEXT)!.valid ? null : this.fgText.get(FC_TEXT_INPUT.TEXT)!.errors : null;
   }
   
   writeValue (value: string | undefined | null): void {
      if (value == undefined) return;
      this.fgText.get(FC_TEXT_INPUT.TEXT)?.setValue(value != undefined ? value : undefined);
      if (typeof value == 'string' && value.trim().length > 0) this.setDirtyTouched();
      this.onValidationChange();
   }

   private setDirtyTouched (): void {
      this.fgText.get(FC_TEXT_INPUT.TEXT)?.markAsDirty(); 
      this.fgText.get(FC_TEXT_INPUT.TEXT)?.markAsTouched();
   }

   registerOnChange (fn: any): void {
      this.onChange = fn
   }
   registerOnTouched (fn: any): void { 
      this.onTouched = fn; 
   }  
  
   onValidationChange: any = () => {}; 

   registerOnValidatorChange? (fn: () => void): void {
      this.onValidationChange = fn;
   }

   private onTouched () {}

   private onChange (_: string) {}

   setDisabledState (isDisabled: boolean | undefined): void { 
      if (this.fgText.get(FC_TEXT_INPUT.TEXT)) isDisabled ? this.setDisabledControls() : this.fgText.get(FC_TEXT_INPUT.TEXT)?.enable();
      this.onValidationChange();
   }

   private reset (): void {
      this.done$$.set(false);
      this.fgText = this.fb.group({});
      this.fieldItem_= {} as IFieldItem;
      this.errorCodes_ = {} as IErrorCodes;
      this.hideRequireMaker = false;
      this.isRequiredMarker.emit(false); 
   }

   private initFormGroup (fieldItem: IFieldItem): boolean {
      if (this.sub_) {
         this.sub_.unsubscribe();
         this.sub_ = undefined;
      }
      
      this.fgText.markAsUntouched();
      this.fgText.markAsPristine();
      
      this.fgText =  this.fb.group({
         [FC_TEXT_INPUT.TEXT]: this.fb.control({value: fieldItem.content, disabled: false}, {validators: this.sValidators.setValidators(this.fieldItem, ITEMTYPE.textInput), updateOn: 'change'})
      })

      this.sub_ = this.fgText.get(FC_TEXT_INPUT.TEXT)?.valueChanges.pipe(
         debounceTime(500)
      ).subscribe(() => {
         if (!this.value && this.fgText.get(FC_TEXT_INPUT.TEXT)?.pristine) return;
         
         this.onChange(this.value);
         this.onTouched();
      })

      this.hideRequireMaker = fieldItem.attribute_1 && fieldItem.attribute_1 != '' ? false : true;
      this.isRequiredMarker.emit(fieldItem.required! && !this.hideRequireMaker);

      return true;
   }
  
   private setDisabledControls (): void {
      this.fgText.get(FC_TEXT_INPUT.TEXT)?.disable();
      if (this.clearValueIfDisabled) this.fgText.get(FC_TEXT_INPUT.TEXT)?.setValue(""); 
      this.fgText.get(FC_TEXT_INPUT.TEXT)?.markAsTouched();
   }
}